import httpRequest from "./index";
import { Customer, CustomerSmsSettingVo, CustomerUpdatePasswordForm, Page } from "@supplier/entity";
import { CustomerForgotPasswordForm, CustomerRegistrationForm } from "@supplier/entity/src/entity/customer";
import { confirmable } from "@/utils/decorator/confirmable";

const PATH = "/customer";

const service = httpRequest;

/**
 * 注册客户账号（供应商端用户注册）
 * @param formData 客户注册表单数据
 * @returns 注册后的客户基础信息
 */
export const registerSupplierCustomerAccount = async (formData: CustomerRegistrationForm): Promise<Customer> => {
    const { data } = await service.post(`${PATH}/registration`, formData);
    return data;
};

/**
 * 获取客户真实姓名（按客户ID查询）
 * @param customerId 客户ID
 * @returns 客户真实姓名字符串
 */
export const getCustomerRealNameById = async (customerId: number): Promise<string> => {
    const { data } = await service.get(`${PATH}/real-name/${customerId}`);
    return data;
};

/**
 * 获取客户真实手机号（按客户ID查询）
 * @param customerId 客户ID
 * @returns 客户真实手机号字符串
 */
export const getCustomerRealMobileById = async (customerId: number): Promise<string> => {
    const { data } = await service.get(`${PATH}/mobile/${customerId}`);
    return data;
};

/**
 * 客户忘记密码重置（找回密码）
 * @param formData 忘记密码表单数据（验证码/新密码等）
 * @returns 密码重置接口响应信息
 */
export const resetCustomerPasswordByForgot = async (formData: CustomerForgotPasswordForm): Promise<string> => {
    const { data } = await service.put(`${PATH}/password/forgot`, formData);
    return data;
};

/**
 * 修改/设置客户登录密码（密码更新）
 * @param formData 密码修改表单数据
 * @returns 密码修改接口响应信息
 */
export const updateCustomerLoginPassword = async (formData: CustomerUpdatePasswordForm): Promise<string> => {
    const { data } = await service.put(`${PATH}/password/modification`, formData);
    return data;
};

/**
 * 获取短信管理的客户及子账号列表数据（支持关键词搜索）
 * @param queryParams 查询参数（搜索关键词<可选>）
 * @returns 包含短信设置VO列表的响应数据
 */
export const getCustomerAndSubAccountForSmsManagement = async (queryParams?: {
    searchParam?: string;
}): Promise<{ customerSmsVos: CustomerSmsSettingVo[] }> => {
    const { data } = await service.get(`${PATH}/customer-sub`, { params: queryParams });
    return data;
};

/**
 * 保存客户及子账号的短信设置（带保存确认弹窗）
 * @param smsSettingList 客户短信设置VO列表
 * @returns 保存操作接口响应数据
 */
export const saveCustomerAndSubAccountSmsSetting = confirmable("是否保存")(async (smsSettingList: CustomerSmsSettingVo[]) => {
    const { data } = await service.post(`${PATH}/customer-sub`, { customerSmsVos: smsSettingList });
    return data;
});

/**
 * 分页查询客户端进销存出入库经办人列表（支持姓名/ID筛选）
 * @param queryParams 查询参数（真实姓名/分页参数/客户ID<可选>）
 * @returns 分页的客户列表数据
 */
export const queryCustomerForInOutBoundOperatorSelector = async (queryParams: {
    realName?: string;
    page?: number;
    size?: number;
    id?: number;
}): Promise<Page<Customer>> => {
    const { data } = await service.get(`${PATH}/selector-bound`, { params: queryParams });
    return data;
};

/**
 * 获取当前客户是否允许换绑手机号。
 * @returns 换绑手机号入口状态，取值对应 CHANGE_MOBILE_STATE。
 */
export const getCustomerAccountState = async (): Promise<number> => {
    const { data } = await service.get(`${PATH}/get/state`);
    return data;
};
